import { useQueryClient } from '@tanstack/react-query';
import {
  getGetInterviewInviteQueryKey,
  getGetInterviewInviteSessionQueryKey,
  getListInterviewInviteSessionsQueryKey,
  useReprocessInterviewInvite,
  useResendInterviewInviteWebhook,
} from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@sara/ui/components/dialog';
import type { DialogBaseProps } from '@sara/ui/components/dialog-provider';
import { toast } from '@sara/ui/components/sonner';
import { ListRestart, Loader2, RotateCcw, Webhook } from 'lucide-react';
import type { ReactNode } from 'react';

interface ReplayDialogProps extends DialogBaseProps {
  inviteId: string;
  /** Whether "Replay everything" is available (a transcript source exists to re-run from scratch). */
  canReprocess: boolean;
}

function ReplayDialog({ inviteId, canReprocess, onClose }: ReplayDialogProps) {
  const queryClient = useQueryClient();

  // After a reprocess is accepted (202), the work runs in the background. Nudge
  // the session views to refetch now and a couple more times as results land,
  // so the operator doesn't have to refresh manually.
  const refreshSessionViews = () => {
    const keys = [
      getGetInterviewInviteQueryKey(inviteId),
      getGetInterviewInviteSessionQueryKey(inviteId),
      getListInterviewInviteSessionsQueryKey(inviteId),
    ];
    const invalidate = () =>
      keys.forEach((queryKey) => void queryClient.invalidateQueries({ queryKey }));
    invalidate();
    [15_000, 45_000].forEach((delay) => setTimeout(invalidate, delay));
  };

  const reprocessMutation = (description: string) => ({
    mutation: {
      onSuccess: () => {
        toast.success('Replay started', { description });
        refreshSessionViews();
        onClose();
      },
      onError: () => toast.error('Could not start the replay. Please try again.'),
    },
  });

  const { mutate: replayFailed, isPending: isReplayingFailed } = useReprocessInterviewInvite(
    reprocessMutation('Re-running the steps that failed — results will refresh shortly.')
  );
  const { mutate: reprocessAll, isPending: isReprocessingAll } = useReprocessInterviewInvite(
    reprocessMutation(
      'Re-scoring the interview from scratch — results will refresh in about a minute.'
    )
  );

  const { mutate: resendWebhook, isPending: isResending } = useResendInterviewInviteWebhook({
    mutation: {
      onSuccess: () => {
        toast.success('Webhook resent');
        onClose();
      },
      onError: () => toast.error('Could not resend the webhook. Please try again.'),
    },
  });

  const busy = isReplayingFailed || isReprocessingAll || isResending;

  return (
    <DialogContent className="sm:max-w-lg">
      <DialogHeader>
        <DialogTitle>Replay this interview</DialogTitle>
        <DialogDescription>
          Re-run the post-call processing, or just re-deliver the results you already have.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3">
        <ReplayOption
          icon={<ListRestart className="h-4 w-4" strokeWidth={1.7} />}
          title="Re-run failed steps"
          description="Re-run only the steps that failed the first time — anything that already succeeded, and the transcript, is kept. Use when part of the automated processing failed (e.g. scoring errored) but everything else is fine."
          action={
            <Button
              size="sm"
              className="gap-1.5"
              disabled={busy}
              onClick={() => replayFailed({ id: inviteId, data: { mode: 'failed' } })}
            >
              {isReplayingFailed ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.7} />
              ) : (
                <ListRestart className="h-3.5 w-3.5" strokeWidth={1.7} />
              )}
              Re-run failed
            </Button>
          }
        />

        <ReplayOption
          icon={<RotateCcw className="h-4 w-4" strokeWidth={1.7} />}
          title="Replay everything"
          description="Re-source the transcript and re-run extraction and rubric scoring from scratch, overwriting the existing results, then send the webhook. Use when the results themselves are wrong — or you changed the template or rubric. Slower and uses AI credits."
          note={
            canReprocess
              ? undefined
              : 'Not available — this session has no transcript source to re-run from scratch.'
          }
          action={
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={!canReprocess || busy}
              onClick={() => reprocessAll({ id: inviteId, data: { mode: 'all' } })}
            >
              {isReprocessingAll ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.7} />
              ) : (
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.7} />
              )}
              Replay
            </Button>
          }
        />

        <ReplayOption
          icon={<Webhook className="h-4 w-4" strokeWidth={1.7} />}
          title="Re-send webhook"
          description="Re-deliver the interview.completed webhook to your configured endpoints using the results already on file. No re-processing. Use when the interview was scored correctly but the downstream system didn't receive it — endpoint was down, misconfigured, or errored."
          action={
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              disabled={busy}
              onClick={() => resendWebhook({ id: inviteId })}
            >
              {isResending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.7} />
              ) : (
                <Webhook className="h-3.5 w-3.5" strokeWidth={1.7} />
              )}
              Re-send
            </Button>
          }
        />
      </div>
    </DialogContent>
  );
}

function ReplayOption({
  icon,
  title,
  description,
  note,
  action,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  note?: string;
  action: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="mt-0.5 inline-flex shrink-0 text-muted-foreground">{icon}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="mt-1 text-[13px] leading-snug text-muted-foreground">{description}</p>
            {note && <p className="mt-2 text-[12px] font-medium text-warning">{note}</p>}
          </div>
        </div>
        <div className="shrink-0 self-center">{action}</div>
      </div>
    </div>
  );
}

export { ReplayDialog };
